import { Fragment, type ReactNode } from "react";
import { LiveDot } from "@design-system/LiveDot/LiveDot";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import type { PresenceTooltipRow, TooltipSections } from "./Tooltip.types";
import styles from "./Tooltip.module.scss";

/** The body fields flattened; `TooltipProps` enforces which combine. */
export interface TooltipBodyFields {
    content?: ReactNode;
    title?: ReactNode;
    sections?: TooltipSections;
    bullets?: readonly ReactNode[];
    shortcut?: string;
    presence?: readonly PresenceTooltipRow[];
}

export type TooltipLayout = "stack" | "shortcut" | "shortcutOnly" | "presence";

const isEmptyNode = (node: ReactNode) =>
    node === null ||
    node === undefined ||
    typeof node === "boolean" ||
    node === "";

const hasShortcut = (shortcut: string | undefined): shortcut is string =>
    shortcut !== undefined && shortcut !== "";

const hasPresence = (
    presence: readonly PresenceTooltipRow[] | undefined,
): presence is readonly PresenceTooltipRow[] =>
    presence !== undefined && presence.length > 0;

/** The sections that have rows; empty ones draw no divider. */
const toFilledSections = (sections: TooltipSections | undefined) =>
    sections?.filter((rows) => rows.length > 0) ?? [];

/** True when the tooltip would render an empty popup. */
export const isTooltipBodyEmpty = ({
    content,
    title,
    sections,
    bullets,
    shortcut,
    presence,
}: TooltipBodyFields) =>
    isEmptyNode(content) &&
    isEmptyNode(title) &&
    toFilledSections(sections).length === 0 &&
    (bullets === undefined || bullets.length === 0) &&
    !hasShortcut(shortcut) &&
    !hasPresence(presence);

/** Which of the popup's padding and flow layouts the body needs. */
export const tooltipLayout = ({
    content,
    shortcut,
    presence,
}: TooltipBodyFields): TooltipLayout => {
    if (hasPresence(presence)) {
        return "presence";
    }
    if (!hasShortcut(shortcut)) {
        return "stack";
    }
    return isEmptyNode(content) ? "shortcutOnly" : "shortcut";
};

/** Label at the start, value at the end, an optional muted `/ detail` after it. */
const SectionRows = ({ sections }: { sections: TooltipSections }) => (
    <div className={styles.sections}>
        {sections.map((rows, index) => (
            <Fragment key={index}>
                {index > 0 && <hr className={styles.sectionDivider} />}
                <dl className={styles.section}>
                    {rows.map(({ label, value, detail }, rowIndex) => (
                        // Labels can repeat (two members named alike).
                        <div key={rowIndex} className={styles.row}>
                            <dt className={styles.rowLabel}>{label}</dt>
                            <dd className={styles.rowValue}>
                                {value}
                                {detail !== undefined && detail !== "" && (
                                    <span className={styles.rowDetail}>
                                        {" / "}
                                        {detail}
                                    </span>
                                )}
                            </dd>
                        </div>
                    ))}
                </dl>
            </Fragment>
        ))}
    </div>
);

/**
 * Popup contents per Figma's Tooltip set: a title, the text, label and value
 * sections, a bullet list, a label followed by key chips, or presence rows.
 */
export const TooltipBody = ({
    content,
    title,
    sections,
    bullets,
    shortcut,
    presence,
}: TooltipBodyFields) => {
    const filledSections = toFilledSections(sections);
    return (
        <>
            {!isEmptyNode(title) && <p className={styles.title}>{title}</p>}
            {!isEmptyNode(content) && (
                <div className={styles.content}>{content}</div>
            )}
            {filledSections.length > 0 && (
                <SectionRows sections={filledSections} />
            )}
            {bullets !== undefined && bullets.length > 0 && (
                <ul className={styles.bullets}>
                    {bullets.map((bullet, index) => (
                        <li key={index}>{bullet}</li>
                    ))}
                </ul>
            )}
            {hasShortcut(shortcut) && <Shortcut keys={shortcut} size="md" />}
            {hasPresence(presence) && (
                <ul className={styles.presenceRows}>
                    {presence.map(({ id, name, detail, isLive }) => (
                        <li key={id} className={styles.presenceRow}>
                            {isLive ? (
                                <LiveDot size="xs" variant="accent" />
                            ) : (
                                <span
                                    aria-hidden
                                    className={styles.presenceRing}
                                />
                            )}
                            <span className={styles.presenceText}>
                                <span className={styles.presenceName}>
                                    {name}
                                </span>
                                {detail !== undefined && detail !== "" && (
                                    <>
                                        <span
                                            aria-hidden
                                            className={styles.presenceSeparator}
                                        >
                                            ·
                                        </span>
                                        <span className={styles.presenceDetail}>
                                            {detail}
                                        </span>
                                    </>
                                )}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </>
    );
};
